import { useEffect, useState } from 'react'
import { collection, onSnapshot } from 'firebase/firestore'
import db from '../../../../../services/firebase'

interface ControlSectionProps {
  selectedTest: any[]
  currentUser: any
  activeFrameworks: any[]
}

const ControlSection: React.FC<ControlSectionProps> = ({
  selectedTest,
  currentUser,
  activeFrameworks
}) => {
  const [controls, setControls] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [showAllFrameworks, setShowAllFrameworks] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!selectedTest.length || !currentUser?.tenantId) return

    const test = selectedTest[0] as any
    if (!test?.id) return

    setLoading(true)

    const tenantId = currentUser.tenantId
    const tenantRef = collection(db, 'tenants', tenantId, 'controls')

    const unsubscribe = onSnapshot(tenantRef, (snapshot) => {
      const controlsData = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }))
        .filter((control: any) => {
          const matchesDocumentId = (() => {
            if (Array.isArray(control.document_id)) {
              return control.document_id.includes(test.id) ||
                     control.document_id.includes(test.test_id) ||
                     control.document_id.some((id: any) => String(id) === String(test.id))
            } else if (typeof control.document_id === 'string') {
              return control.document_id === test.id ||
                     control.document_id === test.test_id ||
                     control.document_id === String(test.id)
            }
            return false
          })()

          const matchesTestId = (() => {
            if (Array.isArray(control.test_id)) {
              return control.test_id.includes(test.id) ||
                     control.test_id.includes(test.test_id) ||
                     control.test_id.some((id: any) => String(id) === String(test.id))
            } else if (typeof control.test_id === 'string') {
              return control.test_id === test.id ||
                     control.test_id === test.test_id ||
                     control.test_id === String(test.id)
            }
            return false
          })()

          return matchesDocumentId || matchesTestId
        })
        .filter((control: any) => {
          if (activeFrameworks.length > 0) {
            return activeFrameworks.some(
              (framework: any) =>
                control.control_framework_mappings &&
                typeof control.control_framework_mappings === 'string' &&
                control.control_framework_mappings.split(',').some((mapping: string) =>
                  mapping.trim().startsWith(framework) &&
                  !mapping.trim().startsWith('ISO 27001:2013')
                )
            )
          }
          return true
        })
      setControls(controlsData)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [selectedTest, currentUser, activeFrameworks])

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Mapped Controls</h4>
        </div>
        <div className="card-body d-flex justify-content-center align-items-center py-10">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="card mt-4">
      <div className="card-body px-4 py-2">
        {controls.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
              <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                  <th className="min-w-200px">CONTROL NAME</th>
                  <th className="min-w-100px">STATUS</th>
                  <th className="min-w-100px">FRAMEWORKS</th>
                </tr>
              </thead>
              <tbody>
                {controls.map((control) => (
                  <tr key={control.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div>
                          <div className="text-dark fw-semibold text-hover-primary fs-6" style={{cursor: 'pointer'}} onClick={() => window.open(`/controls?id=${control.id}`, '_blank')}>
                            {control.control_title || 'Untitled Control'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {control.control_evidence_status ? (
                        <span
                          className={`badge fs-7 ${
                            control.control_evidence_status === 'OK' ||
                            control.control_evidence_status === 'Ok'
                              ? 'badge-light-success'
                              : 'badge-light-danger'
                          }`}
                        >
                          {control.control_evidence_status}
                        </span>
                      ) : (
                        <span className="text-muted fs-8">Unknown</span>
                      )}
                    </td>
                    <td>
                        <div className="d-flex flex-wrap gap-1">
                            <div className=''>
                                {control.control_framework_mappings &&
                                typeof control.control_framework_mappings === 'string' ? (
                                    <span
                                        className='badge px-2 py-1 rounded-pill fs-8'
                                        style={{border: '1px solid #E1E1E1'}}
                                    >
                                        {control.control_framework_mappings.split(',')[0].trim()}{' '}
                                    </span>
                                ) : (
                                    <span className="text-muted fs-8">No framework</span>
                                )}
                            </div>
                            {control.control_framework_mappings &&
                            typeof control.control_framework_mappings === 'string' &&
                            control.control_framework_mappings.split(',').length > 1 &&
                            !showAllFrameworks[control.id] && (
                                <button
                                    className='btn btn-link p-0 ms-1'
                                    onClick={() => setShowAllFrameworks(prev => ({...prev, [control.id]: true}))}
                                >
                                    <span
                                        className='badge px-2 py-1 rounded-pill fs-8'
                                        style={{border: '1px solid #E1E1E1'}}
                                    >
                                        +{control.control_framework_mappings.split(',').length - 1}
                                    </span>
                                </button>
                            )}

                            {showAllFrameworks[control.id] &&
                            control.control_framework_mappings &&
                            typeof control.control_framework_mappings === 'string' && (
                                <>
                                    {control.control_framework_mappings
                                        .split(',')
                                        .slice(1)
                                        .map((framework, index) => (
                                            <span
                                                key={index}
                                                className='badge px-2 py-1 rounded-pill fs-8 mb-1 me-1'
                                                style={{border: '1px solid #E1E1E1'}}
                                            >
                                                {framework.trim()}
                                            </span>
                                        ))}
                                </>
                            )}
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center py-10">
            <div className="symbol symbol-50px mb-4">
              <span className="symbol-label bg-light-primary">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14,2 14,8 20,8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10,9 9,9 8,9" />
                </svg>
              </span>
            </div>
            <h5 className="text-muted">No Controls Mapped</h5>
            <p className="text-muted fs-7 text-center">
              This test is not currently mapped to any controls.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ControlSection
